import { useCallback } from 'react';

import {
  CustomSideType,
  DiagonalCornerOptions,
  SideItemType,
  UserDesignOperation,
} from '../enums';
import { ApplianceModel, ApplianceType } from './../models/appliance.model';

import { useAppDispatch } from '~/redux/hooks';
import { addUserDesignOperation } from '~/redux/slices/undoStack';

export const useRegisterUserAction = () => {
  const dispatch = useAppDispatch();

  const registerUserAction = useCallback(
    (operation: UserDesignOperation) => {
      dispatch(addUserDesignOperation(operation));
    },
    [dispatch]
  );

  const registerCornerAction = useCallback(
    (cornerType: DiagonalCornerOptions | 'cornerRadius') => {
      switch (cornerType) {
        case 'cornerRadius':
          dispatch(
            addUserDesignOperation(UserDesignOperation.MODIFY_CORNER_RADIUS)
          );
          break;
        case DiagonalCornerOptions.INSIDE_CORNER:
          dispatch(
            addUserDesignOperation(
              UserDesignOperation.MODIFY_INSIDE_DIAGONAL_CORNER
            )
          );
          break;
        case DiagonalCornerOptions.OUTSIDE_CORNER:
          dispatch(
            addUserDesignOperation(
              UserDesignOperation.MODIFY_OUTSIDE_DIAGONAL_CORNER
            )
          );
          break;
        case DiagonalCornerOptions.REMOVE_DIAGONAL_CORNER:
          dispatch(
            addUserDesignOperation(UserDesignOperation.REMOVE_DIAGONAL_CORNER)
          );
          break;
        default:
          break;
      }
    },
    [dispatch]
  );

  const registerCustomSideAction = useCallback(
    (customSideType: CustomSideType) => {
      switch (customSideType) {
        case CustomSideType.RADIUS_OUT:
          dispatch(
            addUserDesignOperation(UserDesignOperation.MODIFY_RADIUS_OUT)
          );
          break;
        case CustomSideType.BUMP_OUT:
          dispatch(addUserDesignOperation(UserDesignOperation.MODIFY_BUMP_OUT));
          break;
        case CustomSideType.REMOVE_CUSTOM_SIDE:
          dispatch(
            addUserDesignOperation(UserDesignOperation.REMOVE_CUSTOM_SIDE)
          );
          break;
        default:
          break;
      }
    },
    [dispatch]
  );

  const registerUserApplianceAction = useCallback(
    ({
      appliance,
      applianceType: explicitApplianceType,
    }: {
      appliance?: ApplianceModel;
      applianceType?: ApplianceType;
    }) => {
      const applianceType = appliance?.applianceType ?? explicitApplianceType;

      if (!applianceType) {
        return;
      }

      const isFaucetDisguisedAsElectrical =
        applianceType === ApplianceType.ELECTRICAL_CUTOUT &&
        !explicitApplianceType &&
        !!appliance?.consumerDesc?.startsWith('Faucet');
      const operationByType: Partial<
        Record<ApplianceType, UserDesignOperation>
      > = {
        [ApplianceType.RANGE]: UserDesignOperation.MODIFY_RANGE,
        [ApplianceType.COOKTOP]: UserDesignOperation.MODIFY_COOKTOP,
        [ApplianceType.SINK]: UserDesignOperation.MODIFY_SINK,
        [ApplianceType.FAUCET_CUTOUT]: UserDesignOperation.MODIFY_FAUCET,
        [ApplianceType.ELECTRICAL_CUTOUT]: isFaucetDisguisedAsElectrical
          ? UserDesignOperation.MODIFY_FAUCET
          : UserDesignOperation.MODIFY_ELECTRICAL_CUTOUT,
      };

      const operation = operationByType[applianceType as ApplianceType];

      if (operation) {
        registerUserAction(operation);
      }
    },
    [registerUserAction]
  );

  const registerSideItemAction = useCallback(
    (
      sideType:
        | SideItemType
        | 'backsplashWallSide'
        | 'backsplashHeight'
        | 'waterFallPanelHeight'
    ) => {
      switch (sideType) {
        case SideItemType.WALL_SIDE:
          dispatch(
            addUserDesignOperation(
              UserDesignOperation.MODIFY_SIDE_TYPE_WALL_SIDE
            )
          );
          break;
        case SideItemType.APPLIANCE_SIDE:
          dispatch(
            addUserDesignOperation(
              UserDesignOperation.MODIFY_SIDE_TYPE_APPLIANCE_SIDE
            )
          );
          break;
        case SideItemType.WATERFALL:
          dispatch(
            addUserDesignOperation(
              UserDesignOperation.MODIFY_SIDE_TYPE_WATERFALL_PANEL
            )
          );
          break;
        case 'backsplashWallSide':
          dispatch(
            addUserDesignOperation(
              UserDesignOperation.MODIFY_BACKSPLASH_WALL_SIDE
            )
          );
          break;
        case 'backsplashHeight':
          dispatch(
            addUserDesignOperation(UserDesignOperation.MODIFY_BACKSPLASH_HEIGHT)
          );
          break;
        case 'waterFallPanelHeight':
          dispatch(
            addUserDesignOperation(
              UserDesignOperation.MODIFY_WATERFALL_PANEL_HEIGHT
            )
          );
          break;
        default:
          break;
      }
    },
    [dispatch]
  );

  return {
    registerUserApplianceAction,
    registerCustomSideAction,
    registerCornerAction,
    registerUserAction,
    registerSideItemAction,
  };
};
